import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './StockView.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

async function readApiError(response, fallbackMessage) {
  const data = await response.json().catch(() => ({}));
  return data.error || fallbackMessage;
}

export default function StockView({ token, user }) {
  const [stock, setStock] = useState([]);
  const [groups, setGroups] = useState([]);
  const [summary, setSummary] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('');
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [expandedPlId, setExpandedPlId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const controller = new AbortController();

    const fetchStock = async () => {
      try {
        setRefreshing(true);
        setError('');

        let url = `${API_URL}/inventory/stock`;
        if (availabilityFilter) {
          url += `?availability=${encodeURIComponent(availabilityFilter)}`;
        }

        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(await readApiError(response, 'Failed to load stock inventory'));
        }

        const data = await response.json();
        setStock(data.items || []);
        setGroups(data.groups || []);
        setSummary(data.summary || null);
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }

        const message = err.message === 'Failed to fetch'
          ? 'Unable to load stock for this filter. Check your connection and try again.'
          : err.message;
        setError(message);
      } finally {
        setInitialLoading(false);
        setRefreshing(false);
      }
    };

    fetchStock();

    return () => controller.abort();
  }, [token, availabilityFilter, reloadKey]);

  const statusBadge = (status) => {
    const labels = {
      available: 'Available',
      reserved: 'Reserved',
      delivered: 'Delivered'
    };
    return (
      <span className={`pallet-status status-${status}`}>
        {labels[status] || status}
      </span>
    );
  };

  const plStatusBadge = (status) => {
    const classes = {
      pending_data: 'badge-warning',
      complete: 'badge-info',
      partial: 'badge-primary',
      delivered: 'badge-success'
    };
    const labels = {
      pending_data: 'Pending Data',
      complete: 'In Stock',
      partial: 'Partial Delivery',
      delivered: 'Fully Delivered'
    };
    return (
      <span className={`pl-status-badge ${classes[status] || 'badge-secondary'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (initialLoading) {
    return <div className="stock-view-container"><div className="loading">Loading stock...</div></div>;
  }

  return (
    <div className="stock-view-container">
      <div className="stock-header">
        <div>
          <h1>Warehouse Dashboard</h1>
          <p>On-hand inventory by shipment, material, and pallet</p>
        </div>
        <div className="stock-filters">
          <select
            value={availabilityFilter}
            onChange={(e) => setAvailabilityFilter(e.target.value)}
            disabled={refreshing}
            aria-busy={refreshing}
          >
            <option value="">All stock</option>
            <option value="available">Has available stock</option>
            <option value="partial">Partially delivered</option>
            <option value="depleted">Fully depleted</option>
          </select>
          {refreshing && <span className="stock-filter-loading">Updating...</span>}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger stock-error">
          <span>{error}</span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setReloadKey((current) => current + 1)}
          >
            Retry
          </button>
        </div>
      )}

      {summary && (
        <div className="stock-summary-grid">
          <div className="summary-card">
            <h3>Packing Lists</h3>
            <p className="summary-number">{summary.packing_lists}</p>
          </div>
          <div className="summary-card">
            <h3>Total Pallets</h3>
            <p className="summary-number">{summary.total_pallets}</p>
          </div>
          <div className="summary-card available">
            <h3>Available</h3>
            <p className="summary-number">{summary.available}</p>
          </div>
          <div className="summary-card reserved">
            <h3>Reserved</h3>
            <p className="summary-number">{summary.reserved}</p>
          </div>
          <div className="summary-card delivered">
            <h3>Delivered</h3>
            <p className="summary-number">{summary.delivered}</p>
          </div>
        </div>
      )}

      {!error && groups.length === 0 ? (
        <div className="no-stock">No stock records match the selected filter.</div>
      ) : (
        <div className={`stock-list ${refreshing ? 'stock-list-refreshing' : ''}`}>
          {groups.map((group) => {
            const groupKey = group.shesha_reference || `group-${group.shipment?.shipment_id}`;
            const isGroupExpanded = expandedGroup === groupKey;

            return (
              <div key={groupKey} className="stock-group-card">
                <div
                  className="stock-card-header stock-group-header"
                  onClick={() => setExpandedGroup(isGroupExpanded ? null : groupKey)}
                >
                  <div className="stock-card-title">
                    <h3>{group.shesha_reference || 'No Shesha Reference'}</h3>
                    <div className="stock-card-meta">
                      {group.shipment?.client_name && <span>Client: {group.shipment.client_name}</span>}
                      {group.shipment?.sfr_number && <span>Client Ref/PO: {group.shipment.sfr_number}</span>}
                      <span>{group.packing_lists?.length || 0} packing list(s)</span>
                    </div>
                  </div>
                  <div className="stock-card-badges">
                    <span className="pallet-summary">
                      {group.summary?.available || 0} / {group.summary?.total_pallets || 0} pallets available
                    </span>
                  </div>
                </div>

                {isGroupExpanded && (
                  <div className="stock-group-body">
                    {(group.packing_lists || []).map((item) => (
                      <div key={item.packing_list_id} className="stock-card stock-card-nested">
                        <div
                          className="stock-card-header"
                          onClick={() =>
                            setExpandedPlId(
                              expandedPlId === item.packing_list_id ? null : item.packing_list_id
                            )
                          }
                        >
                          <div className="stock-card-title">
                            <h4>{item.packing_list_reference || `${item.shesha_reference}_${item.invoice_number}`}</h4>
                            <div className="stock-card-meta">
                              {item.supplier?.supplier_name && <span>{item.supplier.supplier_name}</span>}
                              {item.invoice_number && <span>Invoice: {item.invoice_number}</span>}
                            </div>
                          </div>
                          <div className="stock-card-badges">
                            {plStatusBadge(item.status)}
                            <span className={`availability-tag tag-${item.availability}`}>
                              {item.availability}
                            </span>
                            <span className="pallet-summary">
                              {item.summary?.available || 0} / {item.summary?.total_pallets || 0} pallets available
                            </span>
                          </div>
                        </div>

                        {expandedPlId === item.packing_list_id && (
                          <div className="stock-card-body">
                            {item.cargo_items?.length === 0 ? (
                              <p className="no-cargo">No cargo items entered yet.</p>
                            ) : (
                              item.cargo_items.map((cargo) => (
                                <div key={cargo.cargo_id} className="cargo-stock-block">
                                  <div className="cargo-stock-header">
                                    <h4>{cargo.material_description}</h4>
                                    <div className="cargo-qty-summary">
                                      <span>
                                        <strong>{cargo.inventory?.quantity_remaining ?? 0}</strong> of{' '}
                                        {cargo.inventory?.quantity_pieces ?? 0} pcs remaining
                                      </span>
                                      <span>{cargo.inventory?.pallets_available ?? 0} pallets available</span>
                                    </div>
                                  </div>
                                  <div className="pallet-stock-grid">
                                    {(cargo.inventory?.pallets || []).map((pallet) => (
                                      <div
                                        key={pallet.pallet_number}
                                        className={`pallet-stock-item status-${pallet.status}`}
                                      >
                                        <span className="pallet-id">{pallet.pallet_number}</span>
                                        {statusBadge(pallet.status)}
                                        <span className="pallet-qty">{pallet.quantity} pcs</span>
                                        {pallet.delivery_number && (
                                          <span className="pallet-ref">{pallet.delivery_number}</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))
                            )}
                            <div className="stock-card-actions">
                              <Link to={`/packing-list/${item.packing_list_id}/cargo`} className="btn btn-secondary btn-sm">
                                Manage Cargo
                              </Link>
                              {(item.summary?.available || 0) > 0 && (
                                <Link to="/delivery-notes/create" className="btn btn-primary btn-sm">
                                  Create Delivery
                                </Link>
                              )}
                              {(item.summary?.available || 0) > 0 && user?.role === 'admin' && (
                                <Link
                                  to="/delivery-notes"
                                  className="btn btn-secondary btn-sm"
                                  title="Open the delivery note that should include this pallet, then use Admin: Manual Pallet Allocation"
                                >
                                  Allocate missed pallet
                                </Link>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
